import {
  validateSingleColumn,
  validateSingleColumnWithDate,
  validateSingleColumnWithLabels,
  validateColumnMapping,
  validateMultiColumnRulesWithCategories,
  validateMathOperations,
  validateCurrency,
} from './validators';
import { ConfigurationSection, SectionType, SectionId, SectionConfig } from '../types';
import { ValidationResult } from './types';

/**
 * Sections that are optional - if empty, they are valid without configuration
 * Add section IDs here to make them optional across all validators
 */
const OPTIONAL_SECTIONS: SectionId[] = [
  SectionId.SETTLEMENT_ACCOUNT,
  SectionId.SETTLEMENT_CURRENCY,
];

/**
 * Check if a section config is empty based on the section type
 * Returns true if the section has not been configured
 */
const isSectionConfigEmpty = (
  sectionType: SectionType,
  config: SectionConfig
): boolean => {
  switch (sectionType) {
    case SectionType.MULTI_COLUMN_RULES_WITH_CATEGORIES: {
      // Check if categoryRules have no filled rules
      const categoryRules = config.categoryRules || [];
      if (categoryRules.length === 0) return true;

      // Check if all rules in all categories are empty
      return categoryRules.every(categorySection => {
        if (!categorySection.rules || categorySection.rules.length === 0) return true;
        return categorySection.rules.every(rule => {
          if (!rule.columns || rule.columns.length === 0) return true;
          return rule.columns.every(
            column =>
              (!column.sourceColumn || column.sourceColumn === '') &&
              (!column.labels || column.labels.length === 0)
          );
        });
      });
    }

    case SectionType.CURRENCY: {
      // Check if columnMappings is empty or sourceColumn is empty
      const columnMappings = config.columnMappings || [];
      const hasColumn = columnMappings.length > 0 && columnMappings[0]?.sourceColumn;
      const currencyMappings = config.currencyMappings || [];
      const hasCurrencies = currencyMappings.some(
        mapping => mapping.variations && mapping.variations.length > 0
      );
      return !hasColumn && !hasCurrencies;
    }

    default:
      return false;
  }
};

/**
 * Validates a configuration section based on its type
 * This is the single source of truth for validation dispatch logic
 */
export const validateSection = (
  section: ConfigurationSection
): ValidationResult => {
  if (OPTIONAL_SECTIONS.includes(section.id as SectionId)) {
    if (isSectionConfigEmpty(section.type, section.config)) {
      return { isValid: true, errors: [], warnings: [] };
    }
  }

  switch (section.type) {
    case SectionType.SINGLE_COLUMN:
      return validateSingleColumn(section.config, section.id);
    case SectionType.SINGLE_COLUMN_WITH_DATE:
      return validateSingleColumnWithDate(section.config, section.id);
    case SectionType.SINGLE_COLUMN_WITH_LABELS:
      return validateSingleColumnWithLabels(section.config);
    case SectionType.COLUMN_MAPPING:
      return validateColumnMapping(section.config);
    case SectionType.MULTI_COLUMN_RULES_WITH_CATEGORIES:
      return validateMultiColumnRulesWithCategories(section.config);
    case SectionType.MATH_OPERATIONS:
      return validateMathOperations(section.config, section.id);
    case SectionType.CURRENCY:
      return validateCurrency(section.config);
    default:
      return { isValid: true, errors: [], warnings: [] };
  }
};
